function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// Lib Imports
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Button, Calendar, Drop, MaskedInput, Text } from 'grommet';
import { FormPreviousLink, FormNextLink, Schedule, Close } from 'grommet-icons';
import PropTypes from 'prop-types';
// import _ from 'lodash';
import styled from 'styled-components';
import { parseDate, formatDate } from './utils';
import { format } from 'date-fns';
const StyledCalendar = styled(Calendar)`
  .StyledCalendar__StyledWeeksContainer-sc-1y4xhmp-1 {
    box-shadow: none;
  }
`;
const IconBox = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  align-content: center;
  flex-wrap: nowrap;
`;
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DropContent = ({
  dates,
  setDates,
  activeDate,
  setActiveDate,
  onSelect,
  bounds,
  showAjacentDays,
  minYear,
  maxYear,
  showTime,
  customDateFormat,
  ...rest
}) => {
  // const yearRange = _.range(parseInt(minYear), parseInt(maxYear) + 1);
  // const [date, setDate] = useState(new Date());

  const getModifiedDates = dateRange => {
    let [startDate, endDate] = dates ? dates[0] : [null, null];
    if (activeDate === 'end' && startDate && typeof endDate === 'undefined' && typeof dateRange === 'undefined') {
      return [[startDate, startDate]];
    } else if (activeDate === 'start' && endDate && typeof startDate === 'undefined' && typeof dateRange === 'undefined') {
      return [[endDate, endDate]];
    } else return dateRange;
  };
  return /*#__PURE__*/React.createElement(Box, {
    pad: "small",
    round: "xsmall",
    elevation: "small",
    background: "white",
    border: {
      color: 'black'
    },
    overflow: {
      horizontal: 'hidden',
      vertical: 'auto'
    },
    gap: "medium"
  }, /*#__PURE__*/React.createElement(StyledCalendar, _extends({
    activeDate: activeDate,
    dates: dates,
    range: "array",
    animate: false,
    onSelect: dateRange => {
      let val = getModifiedDates(dateRange); // hack to select single date as it is directly not supported by grommet calendar
      setDates(val);
      if (val) onSelect([val[0][0], val[0][1]]);
      if (activeDate === 'start') setActiveDate('end');else setActiveDate('start');
    },
    bounds: bounds,
    daysOfWeek: true,
    showAdjacentDays: showAjacentDays,
    header: ({
      date: currentDate,
      locale,
      onPreviousMonth,
      onNextMonth,
      previousInBound,
      nextInBound
    }) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      align: "center",
      justify: "between"
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: !previousInBound || currentDate.getMonth() === 0 && currentDate.getFullYear() === parseInt(minYear),
      onClick: onPreviousMonth
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(FormPreviousLink, {
      color: "brand"
    }))), /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      align: "center",
      gap: "large"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      primary: activeDate === 'start',
      label: "Start Date",
      onClick: () => setActiveDate('start'),
      style: {
        width: '75px',
        padding: '4px'
      }
    }), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      primary: activeDate === 'end',
      label: "End Date",
      onClick: () => setActiveDate('end'),
      style: {
        width: '75px',
        padding: '4px'
      }
    })), /*#__PURE__*/React.createElement(Button, {
      disabled: !nextInBound || currentDate.getMonth() === 11 && currentDate.getFullYear() === parseInt(maxYear),
      onClick: onNextMonth
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(FormNextLink, {
      color: "brand"
    })))), /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      align: "center",
      justify: "center",
      margin: {
        top: '5px'
      }
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "bold",
      size: "14px",
      color: "#858585"
    }, `${months[currentDate.getMonth()]} - ${currentDate.getFullYear()}`)))
  }, rest)));
};
DropContent.propTypes = {
  date: PropTypes.instanceOf(Date),
  onSelect: PropTypes.func,
  bounds: PropTypes.array,
  showAjacentDays: PropTypes.bool,
  minYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showTime: PropTypes.bool,
  dates: PropTypes.any,
  setDates: PropTypes.func,
  activeDate: PropTypes.any,
  setActiveDate: PropTypes.func,
  customDateFormat: PropTypes.string
};
const availableFormats = ['yyyy-MM-dd', 'dd-MM-yyyy'];
const DateRangePicker = ({
  initialDate,
  onChange,
  showTime,
  bounds: initialBounds,
  minYear,
  maxYear,
  customFormat,
  viewDateFormat,
  boundDays,
  ...rest
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [dates, setDates] = useState(undefined);
  const [activeDate, setActiveDate] = useState('start');
  const [inputValue, setInputValue] = useState('');
  const childRef = useRef();
  const [bounds, setBounds] = useState(initialBounds ? initialBounds : [`${minYear}-01-01`, `${maxYear}-12-31`]);
  let customDateFormat = useMemo(() => {
    if (availableFormats.includes(customFormat)) return customFormat;else return `P${showTime ? ' p' : ''}`;
  }, [customFormat, showTime]);
  const renderDateFormat = useMemo(() => {
    if (viewDateFormat) {
      return showTime ? `${viewDateFormat} p` : viewDateFormat;
    } else {
      return customDateFormat;
    }
  }, [viewDateFormat, showTime, customDateFormat]);
  const inputMask = useMemo(() => {
    const dd = {
      length: [1, 2],
      regexp: /^[1-2][0-9]$|^3[0-1]$|^0?[1-9]$|^0$/,
      placeholder: 'dd'
    };
    const mm = {
      length: [1, 2],
      regexp: /^1[0,1-2]$|^0?[1-9]$|^0$/,
      placeholder: 'mm'
    };
    const yyyy = {
      length: 4,
      //regexp: /^[1-2]$|^19$|^20$|^19[0-9]$|^20[0-9]$|^19[0-9][0-9]$|^20[0-9][0-9]$/,
      placeholder: 'yyyy'
    };
    let separator = null;
    let maskFormat = renderDateFormat.split(' ')[0];
    if (maskFormat.split('-').length === 3) {
      maskFormat = maskFormat.split('-');
      separator = {
        fixed: '-'
      };
    } else {
      maskFormat = maskFormat.split('/');
      separator = {
        fixed: '/'
      };
    }
    let mask = [];
    maskFormat.forEach(i => {
      if (i.toLowerCase() === 'mm') mask.push(mm);else if (i.toLowerCase() === 'dd') mask.push(dd);else if (i.toLowerCase() === 'yyyy') mask.push(yyyy);
      if (mask.length === 1 || mask.length === 3) mask.push(separator); //add separator after first & third entry
    });

    mask.push({
      fixed: ' - '
    });
    mask = mask.concat(mask);
    mask.pop(); // pop out last extra mask
    return mask;
  }, [renderDateFormat]);
  useEffect(() => {
    if (initialDate) {
      setDates([[formatDate(initialDate.split(' - ')[0], customDateFormat), formatDate(initialDate.split(' - ')[1], customDateFormat)]]);
      setInputValue(formatDate(initialDate.split(' - ')[0], renderDateFormat) + ' - ' + formatDate(initialDate.split(' - ')[1], renderDateFormat));
    }
  }, [initialDate, renderDateFormat, customDateFormat]);
  const handleSelect = dates => {
    if (dates && dates[0] && dates[1]) {
      const startDate = new Date(dates[0]);
      const endDate = new Date(dates[1]);
      onChange(customDateFormat !== null ? {
        sDate: format(startDate, customDateFormat),
        eDate: format(endDate, customDateFormat)
      } : {
        sDate: startDate,
        eDate: endDate
      });
      setIsVisible(false);
      if (boundDays) setBounds([format(new Date(dates[0]), 'yyyy-MM-dd'), format(new Date(startDate.setDate(startDate.getDate() + boundDays)), 'yyyy-MM-dd')]);
    } else if (boundDays && dates && dates[0]) {
      let sDate = new Date(dates[0]);
      let eDate = new Date(sDate.setDate(sDate.getDate() + boundDays));
      setBounds([format(new Date(dates[0]), 'yyyy-MM-dd'), format(eDate, 'yyyy-MM-dd')]);
    } else if (boundDays && dates && dates[1]) {
      let eDate = new Date(dates[1]);
      let sDate = new Date(eDate.setDate(eDate.getDate() - boundDays));
      setBounds([format(sDate, 'yyyy-MM-dd'), format(new Date(dates[1]), 'yyyy-MM-dd')]);
    }
  };
  const handleChange = event => {
    const value = event.target.value;
    setInputValue(value);
    if (!value) setDates(undefined);
    let startDate = value.split(' - ')[0] ? parseDate(value.split(' - ')[0], customDateFormat, bounds) : null;
    let endDate = value.split(' - ')[1] ? parseDate(value.split(' - ')[1], customDateFormat, bounds) : null;
    if (startDate && endDate && startDate <= endDate) {
      setDates([[format(startDate, renderDateFormat), format(endDate, renderDateFormat)]]);
    }
  };
  const handleClickOutside = () => {
    if (dates && dates[0][0] && dates[0][1]) {
      onChange(customDateFormat !== null ? {
        sDate: format(new Date(dates[0][0]), customDateFormat),
        eDate: format(new Date(dates[0][1]), customDateFormat)
      } : {
        sDate: dates[0][0],
        eDate: dates[0][1]
      });
    }
    setIsVisible(false);
  };
  const handleBlur = e => {
    let inputDate = convertToJSDate();
    if (inputDate && inputDate.start && inputDate.end && inputDate.start <= inputDate.end) {
      setDates([[format(inputDate.start, customDateFormat), format(inputDate.end, customDateFormat)]]);
      setInputValue(format(inputDate.start, renderDateFormat) + ' - ' + format(inputDate.end, renderDateFormat));
    } else if (dates && dates[0][0] && dates[0][1]) {
      setInputValue(format(new Date(dates[0][0]), renderDateFormat) + ' - ' + format(new Date(dates[0][1]), renderDateFormat));
    } else {
      setInputValue('');
      setDates(undefined);
      onChange(undefined);
    }
  };
  const convertToJSDate = () => {
    let startDate = inputValue.split(' - ')[0] ? parseDate(inputValue.split(' - ')[0], customDateFormat, bounds) : null;
    let endDate = inputValue.split(' - ')[1] ? parseDate(inputValue.split(' - ')[1], customDateFormat, bounds) : null;
    let ret = {
      start: startDate,
      end: endDate
    };
    if (renderDateFormat === 'dd/MM/yyyy') {
      //will convert dd/MM/yyyy to MM/dd/yyyy for parsing
      startDate = inputValue.split(' - ')[0] ? arrangeDateString(inputValue.split(' - ')[0]) : null;
      endDate = inputValue.split(' - ')[1] ? arrangeDateString(inputValue.split(' - ')[1]) : null;
      ret = {
        start: startDate,
        end: endDate
      };
    }
    return ret;
  };
  const arrangeDateString = date => {
    let dateSubParts = date.split('/'); // taking out dd, MM & yyyy component
    if (dateSubParts[0] && dateSubParts[1] && dateSubParts[2] && dateSubParts[2].length === 4) {
      // converting to MM/dd/yyyy
      let fDate = `${dateSubParts[1]}/${dateSubParts[0]}/${dateSubParts[2]}`;
      return parseDate(fDate, customDateFormat, bounds);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    ref: childRef,
    style: {
      marginTop: '10px'
    }
  }, /*#__PURE__*/React.createElement(IconBox, null, /*#__PURE__*/React.createElement(Box, {
    focusIndicator: false,
    flex: "grow",
    onClick: () => setIsVisible(true)
  }, /*#__PURE__*/React.createElement(MaskedInput, {
    mask: inputMask,
    value: inputValue,
    onChange: handleChange,
    onBlur: handleBlur,
    reverse: true
  })), /*#__PURE__*/React.createElement(Box, {
    alignContent: "center",
    direction: "row"
  }, /*#__PURE__*/React.createElement(Close, {
    size: "14px",
    style: {
      marginRight: '8px',
      cursor: 'pointer'
    },
    onClick: () => {
      setInputValue('');
      setDates(undefined);
      onChange(undefined);
    }
  }), /*#__PURE__*/React.createElement(Schedule, {
    size: "18px",
    color: "brand"
  })))), childRef.current && isVisible && /*#__PURE__*/React.createElement(Drop, {
    stretch: false,
    align: {
      top: 'bottom'
    },
    target: childRef.current,
    overflow: "visible",
    onClickOutside: handleClickOutside,
    plain: true
  }, /*#__PURE__*/React.createElement(DropContent, _extends({
    dates: dates,
    setDates: setDates,
    activeDate: activeDate,
    setActiveDate: setActiveDate,
    onSelect: handleSelect,
    showTime: showTime,
    bounds: bounds,
    minYear: minYear,
    maxYear: maxYear,
    customDateFormat: customDateFormat
  }, rest))));
};
DateRangePicker.defaultProps = {
  initialDate: undefined,
  onClose: () => {},
  showAjacentDays: true,
  bounds: undefined,
  minYear: new Date().getFullYear() - 20,
  maxYear: new Date().getFullYear() + 20,
  showTime: false
};
DateRangePicker.propTypes = {
  initialDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onClose: PropTypes.func,
  showAjacentDays: PropTypes.bool,
  bounds: PropTypes.array,
  yearRange: PropTypes.array,
  minYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  showTime: PropTypes.bool,
  customFormat: PropTypes.string,
  viewDateFormat: PropTypes.string,
  boundDays: PropTypes.number
};
export default DateRangePicker;